import {Dict, Req} from 'quickstart/types'
import * as R from 'rambdax'
import {CSSProperties} from 'react'
import {logger} from 'tizra'
import * as fonts from './fonts'
import {makeTypographyTable} from './utils'

const log = logger('quickstart/theme/base/typography')

// This is consumed by components/layout/Container/styles.tsx
export const getContainers = () =>
  ({
    default: {
      // This is the ACTUAL WIDTH of the content, since it applies to the
      // InnerContainer, and the padding below applies to the OuterContainer.
      maxWidths: {
        xs: '86rem',
      },
      // Note that some other components refer to these theme values, especially
      // for full-bleed buttons that want to have the same padding on each side.
      paddings: {
        xs: '1rem',
        sm: '1.5rem',
        md: '2rem',
      },
    },
  } as const)

// This is consumed by components/layout/TextContainer/styles.tsx
//
// These maxWidths correspond to type sizes specified for various breakpoints in
// the TypographyTable below — ideally limiting line lengths to around 75 chars
// for optimal reading.
//
// If you adjust sizes for textMd, then you should update TextContainers
// maxWidths as well.
export const getTextContainers = () =>
  ({
    default: {
      maxWidths: {
        sm: '560px',
        xl: '600px',
        xxl: '720px',
      },
    },
    wider: {
      maxWidths: {
        md: '75%',
      },
    },
  } as const)

const T = makeTypographyTable(`
_ // base
  h1     32/34     0.5 bold
  h2     28/32     0.5 bold
  h3     24/28     0.5 bold
  h4     20/24     0.5 bold
  h5     18/22     0.5 bold
  h6     16/20     0.5 bold
  textSm 14/26     1   regular
  textMd 16/28     1   regular

sm // 568px

md // 768px

lg // 1024px

xl // 1280px
  h1     48/52
  h2     36/40
  h3     32/36
  h4     28/32
  h5     20/24
  h6     18/22
  textSm 16/30
  textMd 18/32

xxl // 1440px
  h5     22/26
  h6     20/24
  textSm 18/34
  textMd 20/36
`)

// prettier-ignore
// font-size, line-height, margin-bottom (ratio of line-height), font-weight
const _TYPOGRAPHY = {
  ...T,
  html:      T.textMd,
  htmlWider: {...T.textMd, height: {_: 28, md: 32, xl: 36, xxl: 40}},
  listMd:    {...T.textMd, margin: 0.5},

  // form typography (includes search facets)
  button:   {size: 16, height: 1, margin: 0, weight: 'bold'},
  form:     {size: 16, height: 1.5, margin: 0.8, weight: 'regular'},
  formMeta: {size: 16, height: 1.5, margin: 0.25, weight: 'regular'},  // also used as margin top
  tag:      {size: 14, height: 1.5, margin: 0, weight: 'bold'},

  // sections
  sectionHead: {...T.h4, margin: 1},
  sectionHeadTight: {...T.h4, margin: 0.5},

  // primary navigation
  nav:        {size: 16, height: 1.5, margin: 0, weight: 'regular'},
  navMeta:    {size: 14, height: 1.5, margin: 0, weight: 'regular'},
  subnavHead: T.h4,
  subnav:     {size: 18, height: 1.5, margin: 0, weight: 'regular'},
  facetHead:  T.h6,

  // content navigation
  cardHead:       T.h5,
  cardSnippet:    {...T.textSm, height: 1.5, margin: 0, weight: 'regular'},
  cardMeta:       {...T.textSm, height: 1.5, margin: 0.125, weight: 'regular'},
  cardCta:        {...T.textSm, height: 1.5, margin: 0, weight: 'bold'},
  summaryHead:    T.h5,
  summarySubhead: {...T.textMd, height: 1.5, margin: 0},
  summarySnippet: {...T.textMd, height: 1.5, margin: 0.5, weight: 'light'},
  summaryMeta:    {...T.textSm, height: 1.5, margin: 0},
  headlineHead:   T.h3,
  tileLabel:      {...T.textSm, height: 1.5, margin: 0.125, weight: 'regular'},
  tileHeadSm:     T.h6,
  tileHeadMd:     T.h5,
  tileHeadLg:     T.h4,
  tileMeta:       {...T.textSm, height: 1.5, margin: 0, weight: 'regular'},
  tileDescription:{...T.textSm, height: 1.5, margin: 0, weight: 'regular'},
  tileCta:        {...T.textSm, height: 1.5, margin: 0, weight: 'bold'},
  digestHeadSm:   T.h6,
  digestHeadMd:   T.h5,
  digestHeadLg:   T.h4,
  digestSnippetLg:{...T.textSm, height: 1.5, margin: 0, weight: 'regular'},
  digestSnippetMd:{...T.textSm, height: 1.5, margin: 0, weight: 'regular'},
  digestSnippetSm:{...T.textSm, height: 1.5, margin: 0, weight: 'regular'},

  // attachments list
  attachment:     {...T.textMd, height: 1.5, margin: 0.5},

  // modals
  modalHead:      T.h5,

  // post and static page typography
  imageCaption:   {...T.textSm, height: 20 / 14, margin: 0.25},
}

// This list must be kept in sync with the typography table.
const variants = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'textSm',
  'textMd',
  'html',
  'htmlWider',
  'listMd',
  'button',
  'form',
  'formMeta',
  'tag',
  'sectionHead',
  'sectionHeadTight',
  'nav',
  'navMeta',
  'subnavHead',
  'subnav',
  'facetHead',
  'cardHead',
  'cardSnippet',
  'cardMeta',
  'cardCta',
  'summaryHead',
  'summarySubhead',
  'summarySnippet',
  'summaryMeta',
  'headlineHead',
  'tileLabel',
  'tileHeadSm',
  'tileHeadMd',
  'tileHeadLg',
  'tileMeta',
  'tileDescription',
  'tileCta',
  'digestHeadSm',
  'digestHeadMd',
  'digestHeadLg',
  'digestSnippetLg',
  'digestSnippetMd',
  'digestSnippetSm',
  'attachment',
  'modalHead',
  'imageCaption',
] as const
export type Variant = (typeof variants)[number]

const props = ['size', 'height', 'margin', 'weight'] as const
type Prop = (typeof props)[number]

const breakpoints = ['_', 'sm', 'md', 'lg', 'xl', 'xxl'] as const
type Breakpoint = (typeof breakpoints)[number]

type Props = {
  size: number
  height: number
  margin: number
  weight: fonts.Weight
}
type ValueOrBreakpoints<T> = T | Req<{[B in Breakpoint]?: T}, '_'>
type TypographyTable = {
  [V in Variant]: {[K in keyof Props]: ValueOrBreakpoints<Props[K]>}
}

export type Rhythm = 'layout' | 'prose'

const TYPOGRAPHY = _TYPOGRAPHY as TypographyTable

export const forTests = {TYPOGRAPHY, variants, props, breakpoints}

type VariantRules = {[k in Variant]: Dict<any>}

export const getTypography = (theme: {
  breakpoints: {[K in Breakpoint as K extends '_' ? never : K]: string}
  toRem: (px: number) => string
}) => {
  const ui = {} as VariantRules
  const spaceAfter = {} as VariantRules

  for (const [_variant, spec] of Object.entries(TYPOGRAPHY)) {
    const variant = _variant as Variant
    const rawValues = {} as Props

    // Assign to a theme object, omitting values that haven't changed since the
    // previous breakpoint.
    const lastValues = {} as Dict<any>
    const assign = ({
      name,
      themeObj,
      key,
      values,
    }: {
      name: string
      themeObj: VariantRules
      key: false | string
      values: any
    }) => {
      if (!key) {
        themeObj[variant] = values
      } else {
        const changed = R.filter(
          (v: any, k: any) =>
            v !== lastValues[name]?.[k as keyof CSSProperties],
          values,
        )
        if (!R.isEmpty(changed)) {
          ;(themeObj[variant] ||= {})[key] = changed
        }
      }
      Object.assign((lastValues[name] ||= {}), values)
    }

    for (const bp of breakpoints) {
      const getRawValue = <P extends Prop, R extends Props[P]>(prop: P): R => {
        const r = (rawValues[prop] =
          typeof spec[prop] === 'object'
            ? // @ts-expect-error doesn't seem to narrow to object
              spec[prop][bp] ?? rawValues[prop]
            : spec[prop])
        return r
      }

      const sizeInPixels = getRawValue('size')
      // prettier-ignore
      const fontSize = `calc(${theme.toRem(sizeInPixels)} * var(${fonts.cssVars.scales.size}))`

      const rawHeight = getRawValue('height')
      const relativeHeightCalc =
        rawHeight < 4 ? `${rawHeight}` : `(${rawHeight} / ${sizeInPixels})`
      // prettier-ignore
      const lineHeight = `calc(${relativeHeightCalc} * var(${fonts.cssVars.scales.height}))`

      const relativeMargin = getRawValue('margin')
      // prettier-ignore
      const marginBottom = `calc(${fontSize} * ${relativeMargin} * ${relativeHeightCalc} * var(${fonts.cssVars.scales.margin}))`

      const weight = getRawValue('weight')
      const fontWeight = `var(${fonts.cssVars.weights[weight]})`

      const mq = bp !== '_' && `@media (min-width: ${theme.breakpoints[bp]})`

      assign({
        name: 'ui',
        themeObj: ui,
        key: mq,
        values: {
          fontSize,
          lineHeight,
          fontWeight,
          // Make a variable for the line-height, so that components can customize
          // spacing based on it.
          '--evergreen-line-height': `calc(${lineHeight} * ${fontSize})`,
        },
      })

      assign({
        name: 'spaceAfter',
        themeObj: spaceAfter,
        key: mq,
        values: {marginBottom},
      })
    }
  }

  return {ui, spaceAfter, prose: R.mergeDeepRight(ui, spaceAfter)}
}
